import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import Select from '@codegouvfr/react-dsfr/Select';
import {
  effectiveMatrices,
  type Matrix,
  type MatrixKind,
  matricesOfKind
} from 'maestro-shared/schema/Matrix/MatrixReferential';
import type { Prescription } from 'maestro-shared/schema/Prescription/Prescription';
import type { ProgrammingPlanChecked } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlans';
import {
  findPrescriptionSubPlan,
  type ProgrammingSubPlanId,
  subPlanLabel
} from 'maestro-shared/schema/ProgrammingPlan/ProgrammingSubPlan';
import { getSubPlanMatrixKinds } from 'maestro-shared/schema/ProgrammingPlan/SubPlanMatrices';
import type { FindSampleOptions } from 'maestro-shared/schema/Sample/FindSampleOptions';
import {
  type SampleStatus,
  SampleStatusLabels,
  SampleStatusList
} from 'maestro-shared/schema/Sample/SampleStatus';
import type { UserListItem } from 'maestro-shared/schema/User/User';
import AppSearchInput from 'src/components/_app/AppSearchInput/AppSearchInput';
import {
  samplersOptions,
  selectOptionsFromList
} from 'src/components/_app/AppSelect/AppSelectOption';
import { useMatrixReferential } from 'src/hooks/useMatrixReferential';
import { t } from 'src/i18n';
import { matrixKindLabels, matrixLabels } from 'src/utils/matrixLabels';
import { pluralize } from '../../utils/stringUtils';

interface Props {
  filters: Partial<FindSampleOptions>;
  onChange: (filters: Partial<FindSampleOptions>) => void;
  programmingPlans?: ProgrammingPlanChecked[];
  samplers?: UserListItem[];
  prescriptions?: Prescription[];
  currentUserId: string | undefined;
}

const SamplePrimaryFilters = ({
  filters,
  onChange,
  programmingPlans,
  samplers,
  prescriptions,
  currentUserId
}: Props) => {
  const matrixReferential = useMatrixReferential();
  const prescriptionMatrixKinds = prescriptions?.flatMap((prescription) =>
    getSubPlanMatrixKinds(
      findPrescriptionSubPlan(programmingPlans ?? [], prescription)?.matrices ??
        null
    )
  );

  return (
    <>
      {(programmingPlans?.length ?? 0) > 1 && (
        <>
          <div className={cx('fr-col-12', 'fr-col-md-3')}>
            <Select
              label="Plan"
              nativeSelectProps={{
                value: '',
                onChange: (e) =>
                  onChange({
                    programmingPlanIds: [
                      ...(filters.programmingPlanIds ?? []),
                      e.target.value as string
                    ]
                  })
              }}
            >
              <option value="">
                {filters.programmingPlanIds?.length
                  ? t('programmingPlan', {
                      count: filters.programmingPlanIds?.length
                    })
                  : 'Tous'}
              </option>
              {programmingPlans
                ?.filter(
                  (plan) => !filters.programmingPlanIds?.includes(plan.id)
                )
                .map((plan) => (
                  <option key={`plan-${plan.id}`} value={plan.id}>
                    {plan.title}
                  </option>
                ))}
            </Select>
          </div>
          <div className={cx('fr-col-12', 'fr-col-md-3')}>
            <Select
              label="Sous-plan"
              nativeSelectProps={{
                value: '',
                onChange: (e) =>
                  onChange({
                    programmingSubPlanIds: [
                      ...(filters.programmingSubPlanIds ?? []),
                      e.target.value as ProgrammingSubPlanId
                    ]
                  })
              }}
            >
              <option value="">
                {filters.programmingSubPlanIds?.length
                  ? pluralize(filters.programmingSubPlanIds.length, {
                      preserveCount: true
                    })('sous-plan')
                  : 'Tous'}
              </option>
              {(filters.programmingPlanIds?.length
                ? programmingPlans?.filter((p) =>
                    filters.programmingPlanIds?.includes(p.id)
                  )
                : programmingPlans
              )
                ?.flatMap((plan) => plan.subPlans)
                .filter((sp) => !filters.programmingSubPlanIds?.includes(sp.id))
                .map((sp) => (
                  <option key={`subPlan-${sp.id}`} value={sp.id}>
                    {subPlanLabel(matrixReferential, sp)}
                  </option>
                ))}
            </Select>
          </div>
        </>
      )}
      <div className={cx('fr-col-12', 'fr-col-md-3')}>
        <AppSearchInput
          value=""
          options={selectOptionsFromList(
            matrixReferential.matrixKinds
              .map(({ code }) => code)
              .filter(
                (matrixKind) =>
                  !filters.matrixKinds?.includes(matrixKind) &&
                  ((filters.programmingPlanIds ?? []).length === 0 ||
                    (filters.contexts ?? []).length === 0 ||
                    !prescriptionMatrixKinds ||
                    prescriptionMatrixKinds.includes(matrixKind))
              ),
            {
              labels: matrixKindLabels(matrixReferential),
              withDefault: false,
              withSort: true
            }
          )}
          placeholder="Sélectionner une catégorie"
          onSelect={(value) => {
            const newMatrixKinds = [
              ...(filters.matrixKinds ?? []),
              value as MatrixKind
            ];
            onChange({
              matrixKinds: newMatrixKinds
            });
          }}
          label="Catégorie de matrice"
          disableAutoSelectSingleOption
          resetOnSelect
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              {option.label}
            </li>
          )}
        />
      </div>
      <div className={cx('fr-col-12', 'fr-col-md-3')}>
        <AppSearchInput
          value=""
          options={selectOptionsFromList(
            effectiveMatrices(matrixReferential).filter(
              (matrix) =>
                !filters.matrices?.includes(matrix) &&
                ((filters.programmingPlanIds ?? []).length === 0 ||
                  (filters.contexts ?? []).length === 0 ||
                  !prescriptionMatrixKinds ||
                  prescriptionMatrixKinds.some((matrixKind) =>
                    matricesOfKind(matrixReferential, matrixKind).includes(
                      matrix
                    )
                  )) &&
                ((filters.matrixKinds ?? []).length === 0 ||
                  filters.matrixKinds!.some((kind) =>
                    matricesOfKind(matrixReferential, kind).includes(matrix)
                  ))
            ),
            {
              labels: matrixLabels(matrixReferential),
              withDefault: false
            }
          )}
          placeholder="Sélectionner une matrice"
          onSelect={(value) => {
            onChange({
              matrices: [...(filters.matrices ?? []), value as Matrix]
            });
          }}
          label="Matrice"
          disableAutoSelectSingleOption
          resetOnSelect
          inputProps={{
            'data-testid': 'matrix-select'
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              {option.label}
            </li>
          )}
        />
      </div>
      <div className={cx('fr-col-12', 'fr-col-md-3')}>
        <Select
          label="Statut"
          nativeSelectProps={{
            value: '',
            onChange: (e) =>
              onChange({
                statuses: [
                  ...(filters.statuses ?? []),
                  e.target.value as SampleStatus
                ]
              })
          }}
        >
          <option value="">
            {filters.statuses?.length
              ? pluralize(filters.statuses.length, { preserveCount: true })(
                  'statut'
                )
              : 'Tous'}
          </option>
          {SampleStatusList.filter(
            (s) => !(filters.statuses ?? []).includes(s)
          ).map((status) => (
            <option key={`status-${status}`} value={status}>
              {SampleStatusLabels[status]}
            </option>
          ))}
        </Select>
      </div>
      <div className={cx('fr-col-12', 'fr-col-md-3')}>
        <Select
          label="Préleveur"
          nativeSelectProps={{
            value: '',
            onChange: (e) =>
              onChange({
                sampledBy: [...(filters.sampledBy ?? []), e.target.value]
              })
          }}
        >
          <option value="">
            {filters.sampledBy?.length
              ? pluralize(filters.sampledBy.length, { preserveCount: true })(
                  'préleveur'
                )
              : 'Tous'}
          </option>
          {samplersOptions(samplers, currentUserId)
            .filter((option) => !filters.sampledBy?.includes(option.value))
            .map((option) => (
              <option key={`sampler-${option.value}`} value={option.value}>
                {option.label}
              </option>
            ))}
        </Select>
      </div>
    </>
  );
};

export default SamplePrimaryFilters;
